import { useEffect, useMemo, useState } from "react"

import useAuthStore from "@features/auth/authStore.client"
import {
  ActionAnchor,
  ActionLink,
  DashboardGrid,
  EmptyState,
  ErrorNotice,
  Ledger,
  LedgerRow,
  Metric,
  Metrics,
  Panel,
  PanelEyebrow,
  PanelText,
  PanelTitle,
} from "@features/portal/PortalPrimitives"
import { serviceRequestsApi } from "@features/portal/serviceRequestsApi"
import { normalizePortalRole, categoryLabel, statusLabel, type ServiceRequest } from "@features/portal/types"
import PortalShell from "../Common/PortalShell"

const formatDate = (date?: string) => {
  if (!date) return "Aujourd’hui"
  return new Intl.DateTimeFormat("fr-CA", { day: "numeric", month: "short" }).format(new Date(date))
}

const Dashboard = () => {
  const user = useAuthStore((state) => state.user)
  const role = normalizePortalRole(user?.role)
  const [requests, setRequests] = useState<ServiceRequest[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")

  useEffect(() => {
    let active = true
    serviceRequestsApi
      .list()
      .then((items) => active && setRequests(items))
      .catch(() => active && setError("Les demandes ne peuvent pas être chargées pour le moment."))
      .finally(() => active && setLoading(false))
    return () => {
      active = false
    }
  }, [])

  const metrics = useMemo(
    () => ({
      open: requests.filter((request) => !["TERMINEE"].includes(request.status)).length,
      planned: requests.filter((request) => request.status === "PLANIFIEE").length,
      completed: requests.filter((request) => request.status === "TERMINEE").length,
    }),
    [requests],
  )

  const firstName = user?.firstName?.trim() || "Bonjour"
  const copy =
    role === "LOCATAIRE"
      ? {
          eyebrow: "Votre logement",
          title: "Une demande claire, au bon endroit.",
          text: "Signalez un bris, joignez des photos et suivez chaque étape sans chercher le bon numéro.",
          action: "Faire une demande",
        }
      : role === "ADMIN"
        ? {
            eyebrow: "Opérations",
            title: "Priorisez ce qui demande votre attention.",
            text: "Les nouvelles demandes, les suivis en cours et les dossiers terminés sont réunis dans un même espace.",
            action: "Voir les demandes",
          }
        : {
            eyebrow: "Vos immeubles",
            title: "Des suivis simples, sans perdre le fil.",
            text: "Consultez les demandes liées à vos immeubles et gardez une vue nette sur les interventions en cours.",
            action: "Consulter les demandes",
          }

  return (
    <PortalShell title={`Bonjour ${firstName}`}>
      <DashboardGrid>
        <Panel $span={8} $dark>
          <PanelEyebrow>{copy.eyebrow}</PanelEyebrow>
          <PanelTitle>{copy.title}</PanelTitle>
          <PanelText>{copy.text}</PanelText>
          <ActionLink to={role === "LOCATAIRE" ? "/app/nouvelle-demande" : "/app/demandes"}>{copy.action}</ActionLink>
          <Metrics>
            <Metric>
              <strong>{loading ? "—" : metrics.open}</strong>
              <span>demande{metrics.open === 1 ? "" : "s"} en cours</span>
            </Metric>
            <Metric>
              <strong>{loading ? "—" : metrics.planned}</strong>
              <span>
                intervention{metrics.planned === 1 ? "" : "s"} planifiée{metrics.planned === 1 ? "" : "s"}
              </span>
            </Metric>
            <Metric>
              <strong>{loading ? "—" : metrics.completed}</strong>
              <span>
                dossier{metrics.completed === 1 ? "" : "s"} terminé{metrics.completed === 1 ? "" : "s"}
              </span>
            </Metric>
          </Metrics>
          {error && <ErrorNotice>{error}</ErrorNotice>}
        </Panel>

        <Panel $span={4}>
          <PanelEyebrow>Aide directe</PanelEyebrow>
          <PanelTitle>Besoin de parler à quelqu’un?</PanelTitle>
          <PanelText>
            Pour une urgence locataire, appelez directement. Pour le reste, une demande écrite garde les détails et les photos au même endroit.
          </PanelText>
          <ActionAnchor href="tel:+14186849004">Appeler le (418) 684-9004</ActionAnchor>
          <br />
          <ActionLink to="/app/aide" $secondary>
            Voir toutes les options
          </ActionLink>
        </Panel>

        <Panel $span={role === "ADMIN" ? 8 : 12}>
          <PanelEyebrow>Activité récente</PanelEyebrow>
          <PanelTitle>Vos derniers suivis</PanelTitle>
          {loading ? (
            <EmptyState>Chargement des suivis…</EmptyState>
          ) : requests.length ? (
            <Ledger>
              {requests.slice(0, 5).map((request) => (
                <LedgerRow key={request._id}>
                  <div>
                    <strong>{categoryLabel[request.category]}</strong>
                    <p>
                      {request.propertyLabel || request.unit || "Demande de service"} · {statusLabel[request.status]}
                    </p>
                  </div>
                  <time dateTime={request.createdAt}>{formatDate(request.createdAt)}</time>
                </LedgerRow>
              ))}
            </Ledger>
          ) : (
            <EmptyState>Aucune demande pour l’instant. Votre historique apparaîtra ici dès qu’un dossier sera soumis.</EmptyState>
          )}
        </Panel>

        {role === "ADMIN" && (
          <Panel $span={4}>
            <PanelEyebrow>Administration</PanelEyebrow>
            <PanelTitle>Accès rapides</PanelTitle>
            <PanelText>Traitez les demandes entrantes, les candidatures et les accès sans quitter l’espace de suivi.</PanelText>
            <ActionLink to="/app/reception">Ouvrir la réception</ActionLink>
            <br />
            <ActionLink to="/app/gestion-des-proprietes" $secondary>
              Immeubles
            </ActionLink>
            <br />
            <ActionLink to="/app/gestion-des-candidatures" $secondary>
              Candidatures
            </ActionLink>
            <br />
            <ActionLink to="/app/acces" $secondary>
              Accès utilisateurs
            </ActionLink>
          </Panel>
        )}
      </DashboardGrid>
    </PortalShell>
  )
}

export default Dashboard
